/**
 * CSV Variable-Safe Formatting Utility
 * 
 * Complies with Certificate & Comment Sheet Data System handover rules:
 * - Variable names use only [A-Za-z0-9]
 * - Must start with a letter (A-Z or a-z); leading numbers are prefixed with 'Var'
 * - No spaces, underscores, hyphens, brackets, %, punctuation, or special characters
 * - Deterministic deduplication (e.g. FinalScore, FinalScore2, FinalScore3)
 * - Strict regex validation against ^[A-Za-z][A-Za-z0-9]*$
 */

/**
 * Convert an arbitrary column header string into a valid, safe variable name.
 * 
 * @param {string} name - Raw header name
 * @returns {string} Variable-safe name matching ^[A-Za-z][A-Za-z0-9]*$
 */
export const cleanVariableName = (name) => {
    if (name === null || name === undefined) return 'Variable';
    
    const raw = String(name).trim();
    if (!raw) return 'Variable';

    // Remove single quotes/apostrophes first so "Teacher's" becomes "Teachers"
    // Also normalize unicode accents (e.g. é -> e) so foreign jury names remain valid
    const normalized = raw
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['’]/g, '');

    // Split on any non-alphanumeric characters to convert to PascalCase
    const parts = normalized
        .split(/[^a-zA-Z0-9]+/)
        .filter(Boolean);

    if (parts.length === 0) return 'Variable';

    // Capitalize each token to form PascalCase
    let pascal = parts
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('');

    // Remove any remaining non-alphanumeric characters
    pascal = pascal.replace(/[^a-zA-Z0-9]/g, '');
    if (!pascal) return 'Variable';

    // Variable must start with an alphabetical letter A-Z or a-z
    if (!/^[a-zA-Z]/.test(pascal)) {
        pascal = 'Var' + pascal;
    }

    return pascal;
};

/**
 * Clean an array of column headers and deduplicate any conflicts deterministically.
 * 
 * @param {string[]} headers - Array of raw column headers
 * @returns {string[]} Array of clean, unique variable-safe headers
 */
export const cleanVariableHeaders = (headers) => {
    if (!Array.isArray(headers)) return [];

    const cleaned = [];
    const used = {};

    headers.forEach((header) => {
        let name = cleanVariableName(header);

        if (used[name] !== undefined) {
            used[name] += 1;
            name = `${name}${used[name]}`;
        } else {
            used[name] = 1;
        }

        cleaned.push(name);
    });

    return cleaned;
};

/**
 * Validate that all column headers conform to the variable-safe specification.
 * 
 * @param {string[]} columns - Array of cleaned column names
 * @throws {Error} If any column name is invalid, empty, or duplicated
 * @returns {boolean} True if all valid
 */
export const validateVariableHeaders = (columns) => {
    if (!Array.isArray(columns) || columns.length === 0) {
        throw new Error('No column headers provided for validation');
    }

    const varRegex = /^[A-Za-z][A-Za-z0-9]*$/;
    const seen = new Set();

    columns.forEach((column, index) => {
        if (!column) {
            throw new Error(`Empty variable name detected at column index ${index}`);
        }

        if (!varRegex.test(column)) {
            throw new Error(
                `Invalid variable name at index ${index}: "${column}". Variable must match ^[A-Za-z][A-Za-z0-9]*$`
            );
        }

        if (seen.has(column)) {
            throw new Error(`Duplicate variable name detected: "${column}"`);
        }
        seen.add(column);
    });

    return true;
};

/**
 * Escape string for safe XML attribute and text content.
 * Strips invalid XML 1.0 control characters that crash Illustrator XML parsing.
 * 
 * @param {any} val 
 * @returns {string}
 */
export const escapeXml = (val) => {
    if (val === null || val === undefined) return '';
    return String(val)
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
};

/**
 * Format multiline text into Illustrator XML <p> tags.
 * Preserves line breaks as separate paragraphs and strips invalid XML control characters.
 * 
 * @param {any} text 
 * @returns {string}
 */
export const formatXmlParagraphs = (text) => {
    if (text === null || text === undefined || text === '') {
        return '<p></p>';
    }
    const cleanText = String(text).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
    const lines = cleanText.split(/\r\n|\r|\n/);
    return lines
        .map((line) => `<p>${escapeXml(line)}</p>`)
        .join('');
};

/**
 * Generate an official Adobe Illustrator Variable Library XML (SVG variableSets format).
 * Directly importable via Adobe Illustrator's native Variables panel (Window > Variables > Import).
 * 
 * @param {Object} options
 * @param {string[]} options.varNames - Array of variable-safe names
 * @param {Array<{ dataSetName: string, values: Record<string, any> }>} options.records - Array of data sets
 * @returns {string} Full XML string conforming to Adobe Illustrator VariableLibrary DTD
 */
export const generateIllustratorVariableXml = ({ varNames = [], records = [] } = {}) => {
    const safeVarNames = cleanVariableHeaders(varNames);
    validateVariableHeaders(safeVarNames);

    const variableDefs = safeVarNames
        .map((varName) => `\t\t\t<variable varName="${varName}" trait="textcontent" category="&ns_custom;"></variable>`)
        .join('\n');

    const dataSetsXml = (records || [])
        .map((record) => {
            const dataSetName = escapeXml(record.dataSetName || 'DataSet');
            const fieldsXml = safeVarNames
                .map((varName) => {
                    const val = record.values ? record.values[varName] : '';
                    return `\t\t\t\t<${varName}>${formatXmlParagraphs(val)}</${varName}>`;
                })
                .join('\n');

            return `\t\t\t<v:sampleDataSet dataSetName="${dataSetName}">\n${fieldsXml}\n\t\t\t</v:sampleDataSet>`;
        })
        .join('\n');

    return `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 20001102//EN" "http://www.w3.org/TR/2000/CR-SVG-20001102/DTD/svg-20001102.dtd" [
\t<!ENTITY ns_graphs "http://ns.adobe.com/Graphs/1.0/">
\t<!ENTITY ns_vars "http://ns.adobe.com/Variables/1.0/">
\t<!ENTITY ns_custom "http://ns.adobe.com/GenericCustomNamespace/1.0/">
\t<!ENTITY ns_flows "http://ns.adobe.com/Flows/1.0/">
\t<!ENTITY ns_extend "http://ns.adobe.com/Extensibility/1.0/">
]>
<svg>
<variableSets xmlns="&ns_vars;">
\t<variableSet varSetName="binding1" locked="none">
\t\t<variables>
${variableDefs}
\t\t</variables>
\t\t<v:sampleDataSets xmlns:v="&ns_vars;" xmlns="&ns_custom;">
${dataSetsXml}
\t\t</v:sampleDataSets>
\t</variableSet>
</variableSets>
</svg>`;
};

/**
 * Format award name for Certificate/Comment Sheet format (e.g. "Silver", "Gold", "Diamond", "Sapphire", "Fail").
 * Strips any "AWARD" / "AWARDS" suffix (e.g. "SILVER AWARD" -> "Silver", "Gold Award" -> "Gold").
 * 
 * @param {string} award 
 * @returns {string}
 */
export const formatCommentSheetAward = (award) => {
    if (!award || award === 'N/A' || award === '—') return '';
    const cleaned = String(award).replace(/\bawards?\b/gi, '').trim();
    if (!cleaned) return '';
    const lower = cleaned.toLowerCase();
    const standardTiers = {
        sapphire: 'Sapphire',
        diamond: 'Diamond',
        gold: 'Gold',
        silver: 'Silver',
        bronze: 'Bronze',
        fail: 'Fail',
    };
    if (standardTiers[lower]) {
        return standardTiers[lower];
    }
    return cleaned;
};

/**
 * Format numeric score for Comment Sheet export (e.g. 87 -> "87.0", 87.33 -> "87.33").
 * 
 * @param {number|string} score 
 * @returns {string}
 */
export const formatCommentSheetScore = (score) => {
    if (score === null || score === undefined || score === '' || isNaN(Number(score))) return '';
    const val = Number(score);
    if (Number.isInteger(val)) {
        return val.toFixed(1);
    }
    const rounded2 = Number(val.toFixed(2));
    if (Number.isInteger(rounded2)) {
        return rounded2.toFixed(1);
    }
    return String(rounded2);
};

/**
 * Format category / age-category string for Comment Sheet export.
 * E.g. "PRIMARY", "FREE CHOICE", "ENSEMBLE - ONE PIANO MULTIPLE HANDS".
 * 
 * @param {Object} row 
 * @param {string} defaultCategory 
 * @returns {string}
 */
export const formatCommentSheetCategory = (row, defaultCategory = '') => {
    if (!row) return (defaultCategory || '').toUpperCase();
    const reg = row.registrant || {};

    if (reg.PerformanceCategory === 'Ensemble') {
        const inst = reg.instrument || reg.category || '';
        const instLower = inst.toLowerCase();
        if (instLower.includes('one_piano') || instLower.includes('one piano')) {
            return 'ENSEMBLE - ONE PIANO MULTIPLE HANDS';
        }
        if (instLower.includes('two_piano') || instLower.includes('two piano') || instLower.includes('two_pianos')) {
            return 'ENSEMBLE-TWO PIANOS MULTIPLE HANDS';
        }
        if (inst) {
            return `ENSEMBLE - ${inst.replace(/_/g, ' ').toUpperCase()}`;
        }
        return 'ENSEMBLE';
    }

    const ageCat = row.ageCategory || reg.ageCategory || '';
    if (ageCat && ageCat !== '—') {
        return ageCat.split('(')[0].trim().toUpperCase();
    }

    return (defaultCategory || '').toUpperCase();
};

/**
 * Slugify a jury name for column headers and score matching.
 * Strips titles/honorifics (e.g. "Dr.", "Prof.", "Mr.", "Ms.", "Mrs."),
 * removes accents, and converts non-alphanumerics to snake_case.
 * 
 * Examples:
 * - "DR. JIN YUN" -> "jin_yun"
 * - "Dr. Jin Yun" -> "jin_yun"
 * - "Livia Schweizer" -> "livia_schweizer"
 * - "András Dénes" -> "andras_denes"
 * 
 * @param {string} name 
 * @returns {string}
 */
export const toJurySlug = (name) => {
    return (name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/^(prof\.?\s+|dr\.?\s+|mr\.?\s+|ms\.?\s+|mrs\.?\s+)+/i, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

/**
 * Extract performer display name from registrant data.
 * For Ensemble or registrations with multiple performers, lists all names joined by ' & '.
 * 
 * @param {Object} reg - Registrant document
 * @returns {string} Formatted performer name(s)
 */
export const getPerformerName = (reg) => {
    if (!reg) return 'Unknown';
    const performers = Array.isArray(reg.performers) ? reg.performers : [];
    const isEnsemble = (reg.PerformanceCategory || reg.performanceCategory || '').toLowerCase() === 'ensemble' || performers.length > 1;

    if (isEnsemble && performers.length > 0) {
        const names = performers
            .map((p) => (p?.fullName || `${p?.firstName || ''} ${p?.lastName || ''}`.trim() || p?.name || '').trim())
            .filter(Boolean);
        if (names.length > 0) {
            return names.join(' & ');
        }
    }

    const p = performers[0];
    if (p) {
        const singleName = (p?.fullName || `${p?.firstName || ''} ${p?.lastName || ''}`.trim() || p?.name || '').trim();
        if (singleName) return singleName;
    }

    return reg.name || 'Unknown';
};

/**
 * Extract song / repertoire name from registrant data.
 * Checks repertoireTitle, repertoire, songTitle, songName, song, pieceTitle, and piece.
 * 
 * @param {Object} reg - Registrant document
 * @returns {string} Clean song/repertoire name or empty string
 */
export const getSongName = (reg) => {
    if (!reg) return '';
    const song = reg.repertoireTitle || reg.repertoire || reg.songTitle || reg.songName || reg.song || reg.pieceTitle || reg.piece || '';
    return typeof song === 'string' ? song.trim() : '';
};
